import {
  ArrowRightIcon,
  CheckCircleIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase';

const Billing = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creditStatus, setCreditStatus] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Stripe Payment Links (Updated with actual Stripe links)
  const STRIPE_PAYMENT_LINKS = {
    ad_hoc: 'https://buy.stripe.com/test_28E14naOE7LTbvXdr83sI03',
    entry: 'https://buy.stripe.com/test_aFadR90a0eah57zdr83sI02',
    mid: 'https://buy.stripe.com/test_8x2bJ17Csfel57zdr83sI01',
    advanced: 'https://buy.stripe.com/test_fZudR91e4aY543vbj03sI00'
  };

  const PRICING_TIERS = [
    {
      id: 'trial',
      name: 'Free Trial',
      price: '$0',
      credits: 30,
      jobs: 3,
      features: [
        '30 credits (3 jobs)',
        'Basic candidate matching',
        'Email support',
        '30-day access',
        'Standard screening reports'
      ],
      current: true,
      cta: 'Current Plan',
      ctaDisabled: true
    },
    {
      id: 'ad_hoc',
      name: 'Ad Hoc',
      price: '$200',
      credits: 50,
      jobs: 5,
      features: [
        '50 credits (5 jobs)',
        'AI-powered sourcing',
        'Basic candidate profiles',
        'Email support',
        '30-day access'
      ],
      cta: 'Purchase Now',
      popular: false
    },
    {
      id: 'entry',
      name: 'Entry Tier',
      price: '$299',
      period: '/month',
      credits: 100,
      jobs: 10,
      features: [
        '100 credits (10 jobs/month)',
        'Communication Agent',
        'Interview Scheduling',
        'Standard screening reports',
        'Email support'
      ],
      cta: 'Start Entry Plan',
      popular: true
    },
    {
      id: 'mid',
      name: 'Mid Tier',
      price: '$699',
      period: '/month',
      credits: 200,
      jobs: 20,
      features: [
        '200 credits (20 jobs/month)',
        'All Entry features',
        'Market Intelligence (5 reports)',
        'Basic analytics dashboard',
        'Priority email support'
      ],
      cta: 'Start Mid Plan',
      popular: false
    },
    {
      id: 'advanced',
      name: 'Advanced Tier',
      price: '$1,499',
      period: '/month',
      credits: 500,
      jobs: 50,
      features: [
        '500 credits (50 jobs/month)',
        'All Mid features',
        'Full Market Intelligence',
        'Campaign Manager included',
        'AI Interview & Video Agent',
        'Dedicated account manager'
      ],
      cta: 'Start Advanced Plan',
      popular: false
    }
  ];

  useEffect(() => {
    fetchCreditStatus();
    fetchPaymentHistory();
  }, []);

  const fetchCreditStatus = async () => {
    try {
      if (!currentUser) return;

      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/credits/status`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCreditStatus(data);
      }
    } catch (error) {
      console.error('Error fetching credit status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      if (!currentUser) return;

      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/billing/payment-history`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentHistory(data.payments || []);
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  const handleUpgrade = (tierId) => {
    if (tierId === 'trial') return;

    const paymentLink = STRIPE_PAYMENT_LINKS[tierId];

    if (!paymentLink || paymentLink.includes('YOUR_')) {
      toast.error('Payment links not configured yet. Please contact support.');
      return;
    }

    // Add client_id as metadata to Stripe payment link
    const urlWithMetadata = `${paymentLink}?client_reference_id=${currentUser?.uid}`;

    // Redirect to Stripe payment page
    window.location.href = urlWithMetadata;
  };

  const openCustomerPortal = async () => {
    try {
      if (!currentUser) return;

      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/billing/customer-portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          return_url: window.location.href
        })
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Unable to open customer portal');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Error opening customer portal');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Billing & Subscription
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Choose the perfect plan for your recruitment needs
          </p>
        </div>

        {/* Current Credit Status */}
        <div className="mb-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Current Balance</h2>
              <div className="flex items-baseline">
                <span className="text-5xl font-bold">{creditStatus?.current_credits || 0}</span>
                <span className="ml-2 text-xl">credits</span>
              </div>
              <p className="mt-2 text-blue-100">
                ≈ {creditStatus?.jobs_possible || 0} jobs available
              </p>
            </div>
            <div className="text-right">
              <CreditCardIcon className="h-16 w-16 opacity-50 mb-2" />
              {creditStatus?.subscription_tier && (
                <button
                  onClick={openCustomerPortal}
                  className="text-sm underline hover:text-blue-200 transition-colors"
                >
                  Manage Subscription
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Pricing Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {PRICING_TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105 ${
                tier.popular ? 'ring-2 ring-blue-600' : ''
              }`}
            >
              {tier.popular && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white px-3 py-1 text-sm font-semibold">
                  POPULAR
                </div>
              )}

              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {tier.name}
                </h3>

                <div className="flex items-baseline mb-4">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    {tier.price}
                  </span>
                  {tier.period && (
                    <span className="ml-1 text-gray-600 dark:text-gray-400">
                      {tier.period}
                    </span>
                  )}
                </div>

                <div className="mb-6 text-sm text-gray-600 dark:text-gray-300">
                  <p className="font-semibold">{tier.credits.toLocaleString()} credits</p>
                  <p>{tier.jobs} jobs</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {tier.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(tier.id)}
                  disabled={tier.ctaDisabled}
                  className={`w-full py-3 px-4 rounded-lg font-semibold flex items-center justify-center transition-colors ${
                    tier.ctaDisabled
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : tier.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600'
                  }`}
                >
                  {tier.cta}
                  {!tier.ctaDisabled && (
                    <ArrowRightIcon className="ml-2 h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Payment History */}
        {paymentHistory.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Payment History
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Credits
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paymentHistory.map((payment, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {payment.tier}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        ${payment.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        +{payment.credits_added}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Back Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:text-blue-700 font-semibold"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default Billing;


